import { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '../../lib/utils';
import { Loader2 } from 'lucide-react';
import { OnboardingBanner } from '../onboarding/OnboardingBanner';
import { ImpersonationBanner } from '../admin/ImpersonationBanner';
import { ForcePasswordRotationDialog } from '../auth/ForcePasswordRotationDialog';
import { VerifyEmailBanner } from '../auth/VerifyEmailBanner';

export const DashboardLayout = () => {
  const { user, loading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // desktop mini/full
  const [mobileOpen, setMobileOpen] = useState(false);               // mobile drawer

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <span className="text-slate-400">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="dashboard-layout">
      <ForcePasswordRotationDialog />

      {/* Mobile overlay when the drawer is open — tap to dismiss */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/60 z-[35]"
          data-testid="mobile-sidebar-overlay"
        />
      )}

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <Header
        collapsed={sidebarCollapsed}
        onMobileMenu={() => setMobileOpen(true)}
      />
      <main
        className={cn(
          "pt-16 min-h-screen transition-all duration-300 overflow-x-hidden",
          // Mobile: no left padding. Desktop: leave room for the sidebar.
          sidebarCollapsed ? "lg:pl-[70px]" : "lg:pl-[260px]"
        )}
      >
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] min-w-0">
          <ImpersonationBanner />
          <VerifyEmailBanner />
          <OnboardingBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
};
